import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ProductDefinitionJson, WizardContexts, WizardTypes } from 'app/shared/models';
import {
  SurveyWizardContext,
  isFolloupWizardContext,
  isSurveyWizardContext,
} from 'app/shared/models/product-wizard.model';
import { ModuleService, ProductService } from 'app/shared/services';
import * as NavigationActions from 'app/state/actions/navigation.actions';
import * as ProductWizardActions from 'app/state/actions/product-wizard.actions';
import { AppState } from 'app/state/app.state';
import {
  initialState as DefaultSurveyWizardState,
  getNextBusinessDay,
  getReminderEmails,
} from 'app/state/reducers/survey-wizard.reducer';
import { EMPTY, forkJoin, from } from 'rxjs';
import { catchError, filter, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import * as SurveyWizardActions from '../actions/survey-wizard.actions';
import { initialState as DefaultFollowupWizardState } from '../reducers/followup-wizard.reducer';

@Injectable()
export class ProductWizardEffects {
  private actions$ = inject(Actions);
  private store = inject<Store<AppState>>(Store);
  private productService = inject(ProductService);
  private moduleService = inject(ModuleService);
  private snackBar = inject(MatSnackBar);
  private translations = inject(TranslocoService);

  createFromSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProductWizardActions.CreateFromSurvey>('[SURVEY_WIZARD] CREATE_FROM_SURVEY'),
      switchMap((action) =>
        // Start from the questions the old survey actually had, not the old product's draft state, which can be
        // stale and points at a module that edits would change in place (#40286). The copy is not saved: the
        // first save creates the new survey's module from it.
        forkJoin([
          this.productService.getById(action.payload.productId),
          this.moduleService.copySurveyQuestionnaire(action.payload.surveyId),
        ]).pipe(
          switchMap(([product, questionnaire]) => {
            const definitionObject = JSON.parse(
              product.companyProductDefinition.definition,
            ) as ProductDefinitionJson;
            const productDefinition = {
              ...product.companyProductDefinition,
              definitionObject: definitionObject,
            };
            const newStateObj: SurveyWizardContext = {
              ...DefaultSurveyWizardState,
              productDefinition: productDefinition,
              // The first save creates the survey from these modules; without them it falls back to the template
              modules: [
                {
                  ...questionnaire,
                  expanded: false,
                  imageUrl: '',
                  excluded: false,
                  selected: false,
                  main: false,
                  preSelected: false,
                },
              ],
              // Set by the first save, to the new survey's module
              moduleId: null,
            };
            return from([
              new ProductWizardActions.CreateFromSurveySuccess(newStateObj),
              new SurveyWizardActions.GetIndexInfo(),
            ]);
          }),
          // The dialog has already closed, so tell the user instead of doing nothing
          catchError(() => {
            this.snackIt(this.translations.translate('INSIGHT.GLOBAL.ERROR.SOMETHINGWRONG'), null);
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  loadDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProductWizardActions.LoadDraft>(ProductWizardActions.Type.LOAD_DRAFT),
      withLatestFrom(
        this.store.select((s) => s.surveyWizardContext),
        this.store.select((s) => s.companyContext),
      ),
      switchMap(([action, surveyWizardContext, companyContext]) => {
        const product$ =
          typeof action.payload === 'number'
            ? // referer is url
              this.productService.getById(action.payload)
            : // referer is product selector
              from([action.payload]);

        return product$.pipe(
          switchMap((product) => {
            let context: WizardContexts;
            if (product === null || product.state === null) {
              if (!surveyWizardContext.productId) {
                // We end up here if we don't have an actual draft, but just a selected product definition
                // so we need to start the zelection flow all over. That will give us a new product Id
                return from([
                  new ProductWizardActions.SelectProductDefinition(
                    product.companyProductDefinition,
                    WizardTypes.survey,
                  ),
                ]);
              }
            } else {
              try {
                context = JSON.parse(product.state, this.reviver) as WizardContexts;
                context.currentVersion = product.currentVersion;
                context.productDefinition = { ...product.companyProductDefinition };
                context.productDefinition.definitionObject = JSON.parse(
                  product.companyProductDefinition.definition,
                );
                if (context.wizardType === WizardTypes.followup) {
                  if (product.actions && product.actions.length >= context.actions.length) {
                    context.actions = product.actions;
                  }
                } else if (context.wizardType === WizardTypes.survey) {
                  this.setupSurveyDates(context);
                }
              } catch (e) {
                this.snackIt(
                  this.translations.translate('INSIGHT.GLOBAL.ERROR.SOMETHINGWRONG'),
                  null,
                );
                console.error('Error parsing draft state', e);
              }
            }

            if (context?.productId === null) {
              context.productId =
                typeof action.payload === 'number' ? action.payload : action.payload.id;
            }
            if (action.forceStep > -1) {
              context.activeStep = action.forceStep;
            }
            // Fix for old drafts /w no name
            if (isSurveyWizardContext(context) && context?.name == null) {
              context.name = context.productDefinition?.name;
            }
            if (isSurveyWizardContext(context)) {
              // const today = new Date();
              context.editMode = true;
              context.wasDeactivated = action.wasDeactivated;

              const loadedFromRoute = !action.editTarget && !action.wasDeactivated;
              if (loadedFromRoute && context.surveyId && context.schedule?.cronExpression) {
                return from([
                  new NavigationActions.Go({
                    path: [companyContext.shortName, 'recurring-surveys'],
                  }),
                ]);
              }

              // The draft is product-wide but a Beat product can own one survey per group, so the row
              // the user opened — not the draft — decides which survey we show and write. Always reset,
              // never leave a stale target behind from the persisted draft.
              const {
                surveyId = null,
                isPaused = false,
                schedule = null,
              } = action.editTarget ?? {};
              context.editedSurveyId = surveyId;
              context.isPaused = isPaused;
              if (schedule) {
                context.schedule = { ...context.schedule, ...schedule };
                this.setupSurveyDates(context);
              }
              // get new user count (setGroups), things in organization might have change, people might have been added or removed
              //  TODO! also; for now we need to save draft after loading it in order for rules to be reapplied

              return from([
                new ProductWizardActions.SetWizardState({ ...context, pristine: false }),
                new SurveyWizardActions.SetGroups(context.participants.groups),
                new ProductWizardActions.SetChanged({ changed: false }),
              ]);
            } else {
              return from([
                new ProductWizardActions.SetWizardState({ ...context, pristine: false }),
                new ProductWizardActions.SetChanged({ changed: false }),
              ]);
            }
          }),
        );
      }),
    ),
  );

  createDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProductWizardActions.CreateDraft>(ProductWizardActions.Type.CREATE_DRAFT),
      withLatestFrom(
        this.store.select((s) => s.companyContext),
        this.store.select((s) => s.surveyWizardContext),
      ),
      switchMap(([action, context, surveyWizardContext]) => {
        let defaultWizardState: WizardContexts;
        let initialDraftState: string = null;

        if (isSurveyWizardContext(action.payload)) {
          const initialState = surveyWizardContext;
          defaultWizardState = {
            ...initialState,
            name: '',
            index: {
              indexValidation: [...initialState.index.indexValidation],
              indexes: initialState.index.indexes ? [...initialState.index.indexes] : null,
              indexInfo: [...action.payload.index.indexInfo],
            },
          };
        }

        if (isFolloupWizardContext(action.payload)) {
          defaultWizardState = this.getInitialState(action.payload.wizardType);
          initialDraftState =
            defaultWizardState != null ? JSON.stringify(defaultWizardState) : null;
        }

        const state: WizardContexts = JSON.parse(JSON.stringify(defaultWizardState), this.reviver);
        state.productDefinition = action.payload.productDefinition;

        return this.productService
          .create(context.id, action.payload.productDefinition.id, initialDraftState)
          .pipe(
            switchMap((product) => {
              return from([
                new ProductWizardActions.SaveDraft({
                  ...state,
                  productId: product.id,
                  currentVersion: product.currentVersion,
                }),
                new ProductWizardActions.SetWizardState({
                  ...state,
                  productId: product.id,
                  currentVersion: product.currentVersion,
                  pristine: true,
                }),
              ]);
            }),
          );
      }),
    ),
  );

  draftSaved$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProductWizardActions.DraftSaved>(ProductWizardActions.Type.DRAFT_SAVED),
      filter((action) => action.showMessage),
      tap(() =>
        this.translations
          .selectTranslate('INSIGHT.GLOBAL.UPDATED')
          .pipe(take(1))
          .subscribe((t: string) => {
            this.snackIt(t + '!', null);
          }),
      ),
      switchMap((_) => from([])),
    ),
  );

  selectProductDefinition$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProductWizardActions.SelectProductDefinition>(
        ProductWizardActions.Type.SELECT_PRODUCT_DEFINITION,
      ),
      switchMap((action) =>
        from([
          new ProductWizardActions.SelectProductDefinitionSuccess(
            action.payload,
            action.wizardType,
          ),
        ]),
      ),
    ),
  );
  setChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProductWizardActions.SetChanged>(ProductWizardActions.Type.SET_CHANGED),
      switchMap((action) => {
        return from([
          new ProductWizardActions.SetChangedSuccess({
            changed: action.payload.changed,
            action: action.payload.action,
          }),
        ]);
      }),
    ),
  );

  actionAfterChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProductWizardActions.SetChangedSuccess>(ProductWizardActions.Type.SET_CHANGED_SUCCESS),
      switchMap((action) => {
        return action.payload.action ? from([action.payload.action]) : from([]);
      }),
    ),
  );

  private snackIt(title: string, desc: string) {
    const popup = this.snackBar.open(title, desc, {
      duration: 3000,
      politeness: 'polite',
      panelClass: 'polite',
    });
    popup.onAction().subscribe(() => {
      popup.dismiss();
    });
  }
  reviver(_, value: any) {
    const dateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/;
    if (typeof value === 'string' && dateFormat.test(value)) {
      return new Date(value);
    }
    return value;
  }

  private getInitialState(wizardType: WizardTypes): WizardContexts {
    switch (wizardType) {
      case WizardTypes.survey:
        return DefaultSurveyWizardState;
      case WizardTypes.followup:
        return DefaultFollowupWizardState;
    }
  }

  private setupSurveyDates(wizardContext: SurveyWizardContext): void {
    const todaysDate = new Date();
    const scheduleContext = wizardContext.schedule;
    const isRecurring =
      wizardContext.productDefinition?.definitionObject?.survey?.recurring === true;
    const resetStartDate =
      !isRecurring &&
      (!this.isValidDate(scheduleContext.startDate) ||
        new Date(scheduleContext.startDate).getTime() < todaysDate.getTime());

    // Reset stale or invalid draft schedules to the same defaults as a new survey.
    if (resetStartDate) {
      scheduleContext.startDate = new Date(
        todaysDate.getFullYear(),
        todaysDate.getMonth(),
        todaysDate.getDate(),
      );
    }

    // Update endDate to 23:59:00 14 days from startDate in case it's before startDate (unless it's null which we allow)
    if (scheduleContext.endDate !== null && scheduleContext.endDate < scheduleContext.startDate) {
      scheduleContext.endDate = new Date(scheduleContext.startDate.getTime());
      scheduleContext.endDate.setDate(scheduleContext.startDate.getDate() + 14);
      scheduleContext.endDate.setHours(23, 59, 0, 0);
    }

    if (resetStartDate && scheduleContext.endDate !== null) {
      scheduleContext.reportDate = getNextBusinessDay(
        new Date(
          scheduleContext.endDate.getFullYear(),
          scheduleContext.endDate.getMonth(),
          scheduleContext.endDate.getDate(),
          9,
          0,
          0,
        ),
      );
    }

    if (scheduleContext.reminderEmails.some((r) => r.date < scheduleContext.startDate)) {
      scheduleContext.reminderEmails = getReminderEmails(
        scheduleContext.startDate,
        scheduleContext.endDate,
        scheduleContext.reminderEmails[0].emailId,
      );
    }
  }

  private isValidDate(d) {
    if (Object.prototype.toString.call(d) === '[object Date]') {
      if (isNaN(d.getTime())) {
        // d.valueOf() could also work
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }
}
